import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';

import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle
} from '@/components/ui/card';
import Error from '@/components/common/Error';
import Spinner from '@/components/common/Spinner';
import Input from '@/components/form/Input';
import HorizontalLogo from '@/components/common/HorizontalLogo';
import { setTenantId } from '@/helpers/api';

import { useActivateUser } from '@/data/user';
import { useActivateTenantUser } from '@/data/tenantUser';

const formSchema = z
  .object({
    firstname: z.string().min(1, { message: 'Required' }),
    lastname: z.string().min(1, { message: 'Required' }),
    password: z
      .string()
      .min(8, { message: 'Password must be at least 8 characters' }),
    confirmPassword: z.string()
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword']
  });

const ActivateForm = ({ user }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState('');
  const navigate = useNavigate();
  const { activationId, tenantUserActivationId } = useParams();
  const activateUser = useActivateUser();
  const activateTenantUser = useActivateTenantUser();

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstname: user.firstname,
      lastname: user.lastname,
      password: '',
      confirmPassword: ''
    }
  });

  const onSubmit = async (values) => {
    console.log(values);
    setApiError('');
    setIsSubmitting(true);
    const payload = {
      firstname: values.firstname,
      lastname: values.lastname,
      password: values.password
    };
    try {
      await activateUser(activationId, payload);
      const tenantUser = await activateTenantUser(tenantUserActivationId);
      setTenantId(tenantUser.tenant.id);
      setIsSubmitting(false);
      navigate('/tenant');
    } catch (err) {
      setIsSubmitting(false);
      setApiError(err.message);
    }
  };

  return (
    <Card className="w-screen max-w-[380px]">
      <CardHeader>
        <div className="p-6">
          <HorizontalLogo />
        </div>
        <CardTitle>Activate Account</CardTitle>
        <CardDescription>
          Set your password and confirm your information to activate your
          account
        </CardDescription>
      </CardHeader>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <CardContent className="space-y-2">
            <div className="flex space-x-4">
              <div className="flex-1">
                <Input name="firstname" label="First Name" autoComplete="off" />
              </div>
              <div className="flex-1">
                <Input name="lastname" label="Last Name" autoComplete="off" />
              </div>
            </div>
            <Input
              name="password"
              label="Password"
              type="password"
              autoComplete="new-password"
            />
            <Input
              name="confirmPassword"
              label="Confirm Password"
              type="password"
              autoComplete="new-password"
            />
            {apiError && <Error error={apiError} />}
          </CardContent>
          <CardFooter>
            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting && <Spinner className="mr-2" />}
              Activate Account
            </Button>
          </CardFooter>
        </form>
      </Form>
    </Card>
  );
};

export default ActivateForm;
